import React, { useEffect, useRef, ReactNode } from 'react'
import { useIntl } from 'react-intl'
import { Typography, makeStyles } from '@material-ui/core/'
import { Formik, Form, FormikProps } from 'formik'
import ResponsiveModal from '@src/components/ResponsiveModal'
import { ResponsiveModalBody } from '@src/components/ResponsiveModal/ResponsiveModal'

import { ccEmailArray } from '@utils/ccEmailArray'
import { useModalAlert } from '@utils/useModalAlert'
import {
  EventMethods,
  sendShareDialogEvent,
} from '@src/routes/OrderCenter/analytics'
import { useCurrentUser } from '@utils/useCurrentUser'
import ShareDialogProductDetails from '@src/components/ShareDialog/ShareDialogProductDetails/ShareDialogProductDetails'
import ShareDialogDirectLink from '@src/components/ShareDialog/ShareDialogDirectLink/ShareDialogDirectLink'
import ShareDialogEmailFields from '@src/components/ShareDialog/ShareDialogEmailFields/ShareDialogEmailFields'
import { Brand, CatalogImage, UserType } from '@src/types/graphql-types'
import { ProductDetailFragment } from '@src/fragments/ProductDetail.generated'
import { useShareDialog } from './useShareDialog'

const useStyles = makeStyles((theme) => ({
  alertBox: { marginBottom: theme.spacing(6) },
}))

export enum ShareDialogItemTypes {
  Order = 'order',
  Quote = 'quote',
  Product = 'product',
}

export type ShareDialogFormValues = {
  relativeShareLink: string
  shareLink: string
  toEmailsRaw: string
  emailFrom: string
}

export type ShareDialogProductDetailsItemProps = {
  brand: Brand
  productName: ProductDetailFragment['name']
  productDescription: ProductDetailFragment['description']
  isMarketplace?: boolean
  productImage?: CatalogImage
}

export type ShareDialogOrderDetailsItemProps = {
  soldTo?: string
  postalCode?: string | null
}

interface ShareDialogProps {
  itemType: ShareDialogItemTypes
  itemNumber: string
  open: boolean
  onClose: (event?: object, reason?: string) => void
  modalHeader: ReactNode
  directLinkDescription?: ReactNode
  product?: ShareDialogProductDetailsItemProps
  order?: ShareDialogOrderDetailsItemProps
}

const ShareDialog: React.FC<ShareDialogProps> = ({
  itemType,
  itemNumber,
  open,
  onClose,
  modalHeader,
  product,
  order,
  directLinkDescription = '',
}) => {
  const classes = useStyles()
  const { currentUser } = useCurrentUser()
  const { formatMessage } = useIntl()
  const formikRef = useRef<FormikProps<ShareDialogFormValues>>(null)

  const { shareQuery, sharePropertyName, shareLoading } = useShareDialog(
    itemType,
    itemNumber,
    product
  )

  const { triggerModalAlert, renderModalAlert, resetModalAlert } =
    useModalAlert({ isModalOpen: open })

  useEffect(() => {
    if (!open) resetModalAlert()
  }, [open, resetModalAlert])

  // BE APIs do not support emailing quotes or orders without an email on your account. CX-2434
  // Additionally, if the user isn't logged in (aka Guest), don't show the email option
  const shouldHideEmail =
    ((itemType === ShareDialogItemTypes.Order ||
      itemType === ShareDialogItemTypes.Quote) &&
      !currentUser?.email) ||
    currentUser?.type === UserType.Guest

  const handleOnCloseDialog = () => {
    onClose()
  }

  const sendEmailAnalytics = () => {
    sendShareDialogEvent({
      detail: itemNumber,
      contentType: itemType,
      method: EventMethods.Email,
      elementText: 'send email',
    })
  }

  const triggerEmailError = () => {
    triggerModalAlert(
      formatMessage({
        id: 'UNABLE_TO_SEND_EMAIL_ERROR',
        defaultMessage:
          'Unable to send email invitations at this time. Please try again.',
      })
    )
  }

  const handleSubmit = async (values: ShareDialogFormValues) => {
    if (shouldHideEmail || !itemNumber) return

    const toEmails = ccEmailArray(values.toEmailsRaw)
    const sharedLink = values.relativeShareLink
    const fromEmail = values.emailFrom
    let success = true

    try {
      const result = await shareQuery(toEmails, fromEmail, sharedLink)
      sendEmailAnalytics()
      if (result.data?.[sharePropertyName] === false) success = false
    } catch {
      success = false
    }
    if (!success) {
      triggerEmailError()
      return
    }
    onClose()
  }

  return (
    <ResponsiveModal
      open={open}
      onClose={handleOnCloseDialog}
      renderTitle={() => (
        <Typography variant="h2" component="h2">
          {modalHeader}
        </Typography>
      )}
    >
      <ResponsiveModalBody>
        <Formik
          initialValues={{
            orderNumber: itemNumber,
            toEmailsRaw: '',
            emailFrom: currentUser?.email || '',
            shareLink: '',
            relativeShareLink: '',
          }}
          validateOnChange={false}
          validateOnBlur={false}
          innerRef={formikRef}
          onSubmit={handleSubmit}
        >
          <Form>
            {renderModalAlert({ className: classes.alertBox })}
            {product && (
              <ShareDialogProductDetails
                itemNumber={itemNumber}
                product={product}
              />
            )}

            <ShareDialogDirectLink
              itemType={itemType}
              itemNumber={itemNumber}
              directLinkDescription={directLinkDescription}
              order={order}
            />

            {!shouldHideEmail && (
              <ShareDialogEmailFields
                itemType={itemType}
                loading={shareLoading}
              />
            )}
          </Form>
        </Formik>
      </ResponsiveModalBody>
    </ResponsiveModal>
  )
}

export default ShareDialog
